import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileText, Download, Search, Send, ChevronLeft,
  MessageSquare, Clock, CheckCircle, XCircle, Loader2, Users,
  Settings, Plus, Trash2, ToggleLeft, ToggleRight, Inbox, Edit, X,
  CreditCard, DollarSign, Eye, Car, Wrench, Hash, Calendar,
  Mail, Phone, Building2, User, Activity, ArrowUpRight, Filter, ShieldCheck
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api/file-service";

const STATUS_CONFIG = {
  pending: { label: "En attente", color: "bg-amber-500/15 text-amber-400 border-amber-500/20", dot: "bg-amber-400" },
  in_progress: { label: "En cours", color: "bg-blue-500/15 text-blue-400 border-blue-500/20", dot: "bg-blue-400" },
  completed: { label: "Terminé", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", dot: "bg-emerald-400" },
  cancelled: { label: "Annulé", color: "bg-red-500/15 text-red-400 border-red-500/20", dot: "bg-red-400" },
};

/* ─────── STAT CARD ─────── */
const StatCard = ({ icon: Icon, label, value, color, accent }) => (
  <div className="bg-[#141414] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4">
    <div className={`w-10 h-10 rounded-lg ${accent} flex items-center justify-center shrink-0`}>
      <Icon size={18} className={color} />
    </div>
    <div>
      <p className="text-white/40 text-[11px] uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  </div>
);

/* ─────── INFO ROW ─────── */
const InfoRow = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-start gap-3 py-2.5">
    <div className={`w-7 h-7 rounded-md ${accent || "bg-white/[0.04]"} flex items-center justify-center shrink-0 mt-0.5`}>
      <Icon size={13} className="text-white/30" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white/35 text-[11px] uppercase tracking-wider">{label}</p>
      <p className="text-white text-sm font-medium truncate">{value || "—"}</p>
    </div>
  </div>
);

/* ════════════════════════════════════════════ */
/*               MAIN COMPONENT                */
/* ════════════════════════════════════════════ */
const FileServiceAdmin = () => {
  const [subTab, setSubTab] = useState("inbox");
  const [requests, setRequests] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [newServiceForm, setNewServiceForm] = useState({ name: "", description: "", base_price: 0 });
  const chatEndRef = useRef(null);

  /* ── Fetchers ── */
  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/requests?search=${search}&status=${statusFilter}`);
      setRequests(res.data);
    } catch (err) { console.error(err); }
  }, [search, statusFilter]);

  const fetchDealers = async () => {
    try { setDealers((await axios.get(`${API}/admin/dealers`)).data); } catch {}
  };
  const fetchServiceTypes = async () => {
    try { setServiceTypes((await axios.get(`${API}/admin/service-types`)).data); } catch {}
  };
  const fetchStats = async () => {
    try { setStats((await axios.get(`${API}/admin/stats`)).data); } catch {}
  };

  useEffect(() => { fetchStats(); fetchRequests(); }, [fetchRequests]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  // ── Real-time admin event stream (SSE) ──
  useEffect(() => {
    let es = null;
    try {
      es = new EventSource(`${API}/admin/events?admin_token=${encodeURIComponent(localStorage.getItem("atr_admin_token") || "")}`);
      es.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt.type === "message") {
            // A new chat message arrived — refresh the requests list badge + if open, append
            fetchRequests();
            setSelectedRequest((sr) => {
              if (sr && sr.id === evt.request_id) {
                setMessages((prev) => prev.some((m) => m.id === evt.data.id) ? prev : [...prev, evt.data]);
                // Toast notification if message from dealer
                if (evt.data.sender_type === "dealer") {
                  toast.success(`Nouveau message de ${evt.data.sender_name || "client"}`);
                }
              } else if (evt.data.sender_type === "dealer") {
                // Notify admin even if not currently viewing that request
                toast.info(`Nouveau message client — demande #${evt.request_id.slice(0, 8)}`);
              }
              return sr;
            });
          } else if (evt.type === "status_changed" || evt.type === "price_changed") {
            fetchRequests();
          }
        } catch {}
      };
      es.onerror = () => {
        // Silent — polling via fetchRequests() interval below covers fallback
      };
    } catch {}
    return () => { if (es) es.close(); };
  }, [fetchRequests]);

  /* ── Actions ── */
  const openRequest = async (req) => {
    setSelectedRequest(req);
    setPriceInput(req.price > 0 ? String(req.price) : "");
    try { setMessages((await axios.get(`${API}/admin/requests/${req.id}/messages`)).data); } catch {}
  };

  const updateStatus = async (requestId, status) => {
    try {
      const fd = new FormData(); fd.append("status", status);
      await axios.put(`${API}/admin/requests/${requestId}/status`, fd);
      toast.success(`Statut : ${STATUS_CONFIG[status]?.label}`);
      fetchRequests(); fetchStats();
      if (selectedRequest?.id === requestId) setSelectedRequest({ ...selectedRequest, status });
    } catch { toast.error("Erreur mise à jour"); }
  };

  const uploadModified = async (requestId, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/admin/requests/${requestId}/upload-modified`, fd);
      toast.success("Fichier modifié uploadé !");
      fetchRequests(); fetchStats();
      if (selectedRequest?.id === requestId)
        setSelectedRequest({ ...selectedRequest, status: "completed", modified_filename: file.name });
    } catch { toast.error("Erreur upload"); }
  };

  const downloadOriginal = async (requestId) => {
    try {
      const res = await axios.get(`${API}/admin/requests/${requestId}/download-original`, { responseType: "blob" });
      const req = requests.find(r => r.id === requestId) || selectedRequest;
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data);
      a.download = req?.original_filename || "original"; a.click();
    } catch { toast.error("Erreur téléchargement"); }
  };

  const setRequestPrice = async () => {
    if (!selectedRequest) return;
    const price = parseFloat(priceInput) || 0;
    try {
      const fd = new FormData(); fd.append("price", price);
      await axios.put(`${API}/admin/requests/${selectedRequest.id}/price`, fd);
      toast.success(`Prix : ${price.toFixed(2)} EUR`);
      setSelectedRequest({ ...selectedRequest, price });
      fetchRequests();
    } catch { toast.error("Erreur prix"); }
  };

  const sendAdminMessage = async () => {
    if (!newMsg.trim() || !selectedRequest) return;
    try {
      const fd = new FormData(); fd.append("message", newMsg);
      const res = await axios.post(`${API}/admin/requests/${selectedRequest.id}/messages`, fd);
      setMessages([...messages, res.data]); setNewMsg("");
    } catch { toast.error("Erreur envoi"); }
  };

  const toggleDealer = async (dealerId) => {
    try { await axios.put(`${API}/admin/dealers/${dealerId}/toggle`); fetchDealers(); } catch {}
  };

  const createServiceType = async () => {
    if (!newServiceForm.name) return;
    try {
      const fd = new FormData();
      fd.append("name", newServiceForm.name);
      fd.append("description", newServiceForm.description);
      fd.append("base_price", newServiceForm.base_price);
      await axios.post(`${API}/admin/service-types`, fd);
      setNewServiceForm({ name: "", description: "", base_price: 0 });
      fetchServiceTypes(); toast.success("Service créé");
    } catch { toast.error("Erreur création"); }
  };

  const deleteServiceType = async (id) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    try { await axios.delete(`${API}/admin/service-types/${id}`); fetchServiceTypes(); toast.success("Supprimé"); } catch {}
  };

  const deleteRequest = async (requestId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette demande ? Les messages et fichiers associés seront aussi supprimés.")) return;
    try {
      await axios.delete(`${API}/admin/requests/${requestId}`);
      if (selectedRequest?.id === requestId) setSelectedRequest(null);
      fetchRequests();
      fetchStats();
      toast.success("Demande supprimée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur suppression");
    }
  };

  const [editingServiceType, setEditingServiceType] = useState(null);
  const [editingDealer, setEditingDealer] = useState(null);

  const saveEditDealer = async () => {
    if (!editingDealer) return;
    try {
      const fd = new FormData();
      fd.append("client_type", editingDealer.client_type || "particulier");
      fd.append("first_name", editingDealer.first_name || "");
      fd.append("last_name", editingDealer.last_name || "");
      fd.append("company_name", editingDealer.company_name || "");
      fd.append("email", editingDealer.email || "");
      fd.append("address_street", editingDealer.address_street || "");
      fd.append("address_postal_code", editingDealer.address_postal_code || "");
      fd.append("address_city", editingDealer.address_city || "");
      fd.append("address_country", editingDealer.address_country || "France");
      fd.append("phone", editingDealer.phone || "");
      await axios.put(`${API}/admin/dealers/${editingDealer.id}`, fd);
      setEditingDealer(null);
      fetchDealers();
      toast.success("Client mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur mise à jour");
    }
  };

  const resetDealerPassword = async () => {
    if (!editingDealer) return;
    const pwd = window.prompt(`Nouveau mot de passe pour ${editingDealer.email} (min 6 caractères) :`);
    if (!pwd) return;
    if (pwd.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("new_password", pwd);
      await axios.put(`${API}/admin/dealers/${editingDealer.id}/password`, fd);
      toast.success("Mot de passe réinitialisé — communiquez-le au client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };
  const startEditService = (st) => setEditingServiceType({ ...st });
  const saveEditService = async () => {
    if (!editingServiceType) return;
    try {
      const fd = new FormData();
      fd.append("name", editingServiceType.name);
      fd.append("description", editingServiceType.description || "");
      fd.append("base_price", editingServiceType.base_price);
      fd.append("is_active", editingServiceType.is_active ? "true" : "false");
      await axios.put(`${API}/admin/service-types/${editingServiceType.id}`, fd);
      setEditingServiceType(null);
      fetchServiceTypes();
      toast.success("Service mis à jour");
    } catch { toast.error("Erreur mise à jour"); }
  };

  /* ════════════ RENDER ════════════ */
  return (
    <div data-testid="fs-admin" className="space-y-5">

      {/* ── STATS ── */}
      {stats && (
        <div className="grid grid-cols-5 gap-3">
          <StatCard icon={Hash} label="Total" value={stats.total_requests} color="text-white" accent="bg-white/[0.06]" />
          <StatCard icon={Clock} label="En attente" value={stats.pending} color="text-amber-400" accent="bg-amber-500/10" />
          <StatCard icon={Activity} label="En cours" value={stats.in_progress} color="text-blue-400" accent="bg-blue-500/10" />
          <StatCard icon={CheckCircle} label="Terminés" value={stats.completed} color="text-emerald-400" accent="bg-emerald-500/10" />
          <StatCard icon={Users} label="Clients" value={stats.total_dealers} color="text-purple-400" accent="bg-purple-500/10" />
        </div>
      )}

      {/* ── SUB TABS ── */}
      <div className="flex items-center gap-1 bg-[#141414] rounded-xl p-1 w-fit">
        {[
          { id: "inbox", label: "Inbox", icon: Inbox, action: fetchRequests },
          { id: "dealers", label: "Clients", icon: Users, action: fetchDealers },
          { id: "services", label: "Tarifs", icon: Settings, action: fetchServiceTypes },
        ].map(tab => (
          <button
            key={tab.id}
            data-testid={`fs-admin-tab-${tab.id}`}
            onClick={() => { setSubTab(tab.id); setSelectedRequest(null); tab.action(); }}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              subTab === tab.id
                ? "bg-[#FF3B30] text-white shadow-lg shadow-red-500/20"
                : "text-white/40 hover:text-white/70"
            }`}
          >
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {/* ══════════════════ INBOX ══════════════════ */}
      {subTab === "inbox" && !selectedRequest && (
        <div className="space-y-4">
          {/* Search & Filter */}
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/20" />
              <input
                data-testid="fs-admin-search"
                type="text" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher par client, véhicule, VIN..."
                className="w-full bg-[#141414] border border-white/[0.06] rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#FF3B30]/30 focus:outline-none"
              />
            </div>
            <div className="relative">
              <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" />
              <select
                data-testid="fs-admin-status-filter"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="bg-[#141414] border border-white/[0.06] rounded-xl pl-9 pr-8 py-2.5 text-white text-sm appearance-none cursor-pointer"
              >
                <option value="">Tous</option>
                <option value="pending">En attente</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Terminé</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="bg-[#141414] border border-white/[0.06] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {["#", "Client", "Véhicule", "VIN", "Service", "Statut", "Prix", "Date", ""].map(h => (
                    <th key={h} className="text-left text-white/30 text-[11px] uppercase tracking-wider font-medium px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-12 text-white/20 text-sm">Aucune demande de fichier</td></tr>
                ) : requests.map((req, i) => {
                  const st = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                  return (
                    <tr
                      key={req.id}
                      data-testid={`fs-admin-request-${i}`}
                      className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer transition-colors group"
                      onClick={() => openRequest(req)}
                    >
                      <td className="px-4 py-3.5 text-white/20 text-xs font-mono">{requests.length - i}</td>
                      <td className="px-4 py-3.5">
                        <p className="text-white text-sm font-medium">{req.dealer_name}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="text-white text-sm">{req.vehicle_brand} {req.vehicle_model}</p>
                        <p className="text-white/25 text-xs">{req.vehicle_year}</p>
                      </td>
                      <td className="px-4 py-3.5 text-white/40 text-xs font-mono">{req.vin || "—"}</td>
                      <td className="px-4 py-3.5 text-[#FF3B30] text-sm font-medium">{req.service_type_name || "—"}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${st.color}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        {req.price > 0 ? (
                          <span className={`text-sm font-semibold ${req.is_paid ? "text-emerald-400" : "text-amber-400"}`}>
                            {req.price.toFixed(2)} €
                          </span>
                        ) : <span className="text-white/15 text-sm">—</span>}
                      </td>
                      <td className="px-4 py-3.5 text-white/30 text-xs">
                        {new Date(req.created_at).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={(e) => deleteRequest(req.id, e)}
                            className="text-white/15 hover:text-red-400 transition-colors p-1.5 rounded hover:bg-red-500/10"
                            title="Supprimer la demande"
                            data-testid={`fs-admin-delete-${i}`}
                          >
                            <Trash2 size={14} />
                          </button>
                          <ArrowUpRight size={14} className="text-white/10 group-hover:text-white/40 transition-colors" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════════ REQUEST DETAIL ══════════════════ */}
      {subTab === "inbox" && selectedRequest && (() => {
        const st = STATUS_CONFIG[selectedRequest.status] || STATUS_CONFIG.pending;
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-white/30 hover:text-white text-sm flex items-center gap-1.5 transition-colors"
              >
                <ChevronLeft size={14} /> Retour à l'inbox
              </button>
              <button
                onClick={() => deleteRequest(selectedRequest.id)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-red-500/20 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                data-testid="fs-admin-delete-detail"
              >
                <Trash2 size={13} /> Supprimer la demande
              </button>
            </div>

            <div className="grid lg:grid-cols-[1fr_320px] gap-4">
              {/* ── LEFT COLUMN ── */}
              <div className="space-y-4">

                {/* Header */}
                <div className="bg-[#141414] border border-white/[0.06] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-white text-lg font-bold">
                        {selectedRequest.vehicle_brand} {selectedRequest.vehicle_model} {selectedRequest.vehicle_year}
                      </h2>
                      <p className="text-white/30 text-xs mt-1">{selectedRequest.dealer_name} — {selectedRequest.dealer_email}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${st.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                      {st.label}
                    </span>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-0 border-t border-white/[0.04] pt-4">
                    <InfoRow icon={Car} label="Motorisation" value={selectedRequest.vehicle_engine} />
                    <InfoRow icon={Hash} label="VIN" value={selectedRequest.vin} />
                    <InfoRow icon={Settings} label="Type ECU" value={selectedRequest.ecu_type} />
                    <InfoRow icon={Settings} label="Transmission" value={selectedRequest.transmission} />
                    <InfoRow icon={Wrench} label="Outil" value={selectedRequest.tool} />
                    <InfoRow icon={FileText} label="Service" value={selectedRequest.service_type_name} accent="bg-[#FF3B30]/10" />
                    <InfoRow icon={FileText} label="Fichier original" value={selectedRequest.original_filename} />
                    <InfoRow icon={User} label="Type client" value={selectedRequest.client_type || "professionnel"} />
                  </div>

                  {selectedRequest.notes && (
                    <div className="mt-4 p-3 bg-white/[0.02] border border-white/[0.04] rounded-lg">
                      <p className="text-white/30 text-[11px] uppercase tracking-wider mb-1">Notes client</p>
                      <p className="text-white/70 text-sm">{selectedRequest.notes}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="bg-[#141414] border border-white/[0.06] rounded-xl p-5">
                  <h3 className="text-white font-semibold text-sm mb-4">Actions</h3>

                  {/* File actions */}
                  <div className="flex gap-2 mb-5">
                    <button
                      data-testid="fs-admin-download-original"
                      onClick={() => downloadOriginal(selectedRequest.id)}
                      className="bg-white/[0.06] hover:bg-white/[0.1] text-white px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 transition-colors"
                    >
                      <Download size={14} /> Télécharger original
                    </button>
                    <label className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm flex items-center gap-2 cursor-pointer transition-colors">
                      <Upload size={14} /> Uploader fichier modifié
                      <input type="file" className="hidden"
                        onChange={e => e.target.files?.[0] && uploadModified(selectedRequest.id, e.target.files[0])} />
                    </label>
                  </div>

                  {/* Status buttons */}
                  <div className="mb-5">
                    <p className="text-white/30 text-[11px] uppercase tracking-wider mb-2">Changer le statut</p>
                    <div className="flex gap-2">
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <button
                          key={key}
                          data-testid={`fs-admin-status-${key}`}
                          onClick={() => updateStatus(selectedRequest.id, key)}
                          disabled={selectedRequest.status === key}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                            selectedRequest.status === key
                              ? `${cfg.color} opacity-100`
                              : "border-white/[0.06] text-white/30 hover:text-white/60 hover:border-white/10"
                          } disabled:cursor-default`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${selectedRequest.status === key ? cfg.dot : "bg-white/20"}`} />
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="border-t border-white/[0.04] pt-4">
                    <p className="text-white/30 text-[11px] uppercase tracking-wider mb-2">Prix du fichier</p>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          data-testid="fs-admin-price-input"
                          type="number" step="0.01" min="0"
                          value={priceInput} onChange={e => setPriceInput(e.target.value)}
                          placeholder="0.00"
                          className="bg-[#1C1C1E] border border-white/[0.06] rounded-lg pl-3 pr-10 py-2.5 text-white text-sm w-36 focus:border-[#FF3B30]/30 focus:outline-none"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 text-xs">EUR</span>
                      </div>
                      <button
                        data-testid="fs-admin-set-price"
                        onClick={setRequestPrice}
                        className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                      >
                        Appliquer
                      </button>
                      {selectedRequest.price > 0 && (
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-white/40 text-xs">Actuel :</span>
                          <span className="text-white font-bold text-sm">{selectedRequest.price.toFixed(2)} €</span>
                          {selectedRequest.is_paid
                            ? <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/20">Payé</span>
                            : <span className="text-[10px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/20">Non payé</span>
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT COLUMN: CHAT ── */}
              <div className="bg-[#141414] border border-white/[0.06] rounded-xl flex flex-col h-[580px]">
                <div className="px-5 py-4 border-b border-white/[0.04] flex items-center gap-2">
                  <MessageSquare size={14} className="text-[#FF3B30]" />
                  <h3 className="text-white font-semibold text-sm">Conversation</h3>
                  <span className="text-white/20 text-xs ml-auto">{messages.length} msg</span>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-white/15">
                      <MessageSquare size={32} className="mb-2" />
                      <p className="text-xs">Aucun message</p>
                    </div>
                  ) : messages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.sender_type === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                        msg.sender_type === "admin"
                          ? "bg-[#FF3B30] text-white rounded-br-md"
                          : "bg-white/[0.06] text-white/80 rounded-bl-md"
                      }`}>
                        <p className="text-[10px] opacity-50 mb-0.5">{msg.sender_name}</p>
                        <p className="text-sm leading-relaxed">{msg.message}</p>
                        <p className="text-[10px] opacity-30 mt-1 text-right">
                          {new Date(msg.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>

                <div className="p-3 border-t border-white/[0.04]">
                  <div className="flex gap-2">
                    <input
                      data-testid="fs-admin-chat-input"
                      type="text" value={newMsg}
                      onChange={e => setNewMsg(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && sendAdminMessage()}
                      placeholder="Répondre au client..."
                      className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded-xl px-4 py-2.5 text-white text-sm focus:border-[#FF3B30]/30 focus:outline-none"
                    />
                    <button
                      data-testid="fs-admin-chat-send"
                      onClick={sendAdminMessage}
                      className="bg-[#FF3B30] hover:bg-[#D63025] text-white p-2.5 rounded-xl transition-colors"
                    >
                      <Send size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══════════════════ CLIENTS ══════════════════ */}
      {subTab === "dealers" && (
        <div className="bg-[#141414] border border-white/[0.06] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {["Client", "Contact", "Email", "Téléphone", "Fichiers", "Statut", ""].map(h => (
                  <th key={h} className="text-left text-white/30 text-[11px] uppercase tracking-wider font-medium px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dealers.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-white/20 text-sm">Aucun client inscrit</td></tr>
              ) : dealers.map(d => (
                <tr key={d.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center">
                        <Building2 size={14} className="text-white/30" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {d.company_name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || "—"}
                        </p>
                        <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded ${
                          d.client_type === "professionnel"
                            ? "bg-blue-500/15 text-blue-400 border border-blue-500/20"
                            : "bg-purple-500/15 text-purple-400 border border-purple-500/20"
                        }`}>
                          {d.client_type === "professionnel" ? "Pro" : "Particulier"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-white/60 text-sm">
                    <p>{d.contact_name || `${d.first_name || ""} ${d.last_name || ""}`.trim()}</p>
                    {(d.address_city || d.address_postal_code) && (
                      <p className="text-[11px] text-white/35 mt-0.5 truncate max-w-[180px]">
                        {[d.address_street, d.address_postal_code, d.address_city].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-white/40 text-sm">{d.email}</td>
                  <td className="px-5 py-4 text-white/40 text-sm">{d.phone || "—"}</td>
                  <td className="px-5 py-4">
                    <span className="text-white text-sm font-bold">{d.total_files || 0}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
                      d.is_active
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                        : "bg-red-500/15 text-red-400 border-red-500/20"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${d.is_active ? "bg-emerald-400" : "bg-red-400"}`} />
                      {d.is_active ? "Actif" : "Désactivé"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingDealer({ ...d })}
                        className="text-white/30 hover:text-blue-400 transition-colors"
                        title="Modifier les informations"
                        data-testid={`fs-admin-edit-dealer-${d.id}`}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => toggleDealer(d.id)}
                        className="text-white/20 hover:text-white/60 transition-colors"
                        title={d.is_active ? "Désactiver" : "Activer"}
                      >
                        {d.is_active ? <ToggleRight size={20} className="text-emerald-400" /> : <ToggleLeft size={20} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ══════════════════ TARIFS ══════════════════ */}
      {subTab === "services" && (
        <div className="space-y-4">
          {/* Add form */}
          <div className="bg-[#141414] border border-white/[0.06] rounded-xl p-5">
            <h3 className="text-white font-semibold text-sm mb-4">Ajouter un service</h3>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-white/30 text-[11px] uppercase tracking-wider block mb-1.5">Nom du produit</label>
                  <input
                    data-testid="fs-admin-service-name"
                    type="text" value={newServiceForm.name}
                    onChange={e => setNewServiceForm({ ...newServiceForm, name: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#FF3B30]/30 focus:outline-none"
                    placeholder="Ex: Custom Tune - Stage 2"
                  />
                </div>
                <div className="w-40">
                  <label className="text-white/30 text-[11px] uppercase tracking-wider block mb-1.5">Prix (EUR)</label>
                  <input
                    data-testid="fs-admin-service-price"
                    type="number" step="0.01" value={newServiceForm.base_price}
                    onChange={e => setNewServiceForm({ ...newServiceForm, base_price: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-2.5 text-white text-sm focus:border-[#FF3B30]/30 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-white/30 text-[11px] uppercase tracking-wider block mb-1.5">Information / Description</label>
                <textarea
                  value={newServiceForm.description}
                  onChange={e => setNewServiceForm({ ...newServiceForm, description: e.target.value })}
                  rows={2}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-lg px-4 py-2.5 text-white text-sm resize-none focus:border-[#FF3B30]/30 focus:outline-none"
                  placeholder="Description détaillée du service visible par le client..."
                />
              </div>
              <div className="flex justify-end">
                <button
                  data-testid="fs-admin-add-service"
                  onClick={createServiceType}
                  className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-6 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  <Plus size={14} /> Ajouter
                </button>
              </div>
            </div>
          </div>

          {/* Services list */}
          <div className="bg-[#141414] border border-white/[0.06] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {["Service", "Description", "Prix", "Actif", ""].map(h => (
                    <th key={h} className="text-left text-white/30 text-[11px] uppercase tracking-wider font-medium px-5 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {serviceTypes.map(st => (
                  <tr key={st.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                    <td className="px-5 py-4 text-white text-sm font-medium">{st.name}</td>
                    <td className="px-5 py-4 text-white/35 text-xs max-w-xs">{st.description || "—"}</td>
                    <td className="px-5 py-4 text-white text-sm font-bold">{st.base_price > 0 ? `${st.base_price.toFixed(2)} €` : "0,00 €"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${
                        st.is_active
                          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                          : "bg-red-500/15 text-red-400 border-red-500/20"
                      }`}>
                        {st.is_active ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditService(st)}
                          className="text-white/40 hover:text-blue-400 transition-colors"
                          title="Modifier"
                        >
                          <Edit size={14} />
                        </button>
                        <button onClick={() => deleteServiceType(st.id)} className="text-white/15 hover:text-red-400 transition-colors" title="Supprimer">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── EDIT DEALER MODAL ── */}
      {editingDealer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#141414] border border-white/10 rounded-xl w-full max-w-xl my-8">
            <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-[#141414]/95 backdrop-blur">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Edit size={16} className="text-blue-400" /> Modifier le client
              </h3>
              <button onClick={() => setEditingDealer(null)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Type */}
              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Type de client</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "particulier", label: "Particulier" },
                    { value: "professionnel", label: "Professionnel" },
                  ].map((opt) => (
                    <button key={opt.value} type="button"
                      onClick={() => setEditingDealer({ ...editingDealer, client_type: opt.value })}
                      className={`py-2 rounded-md text-sm font-medium border transition-all ${
                        editingDealer.client_type === opt.value
                          ? "bg-[#FF3B30] text-white border-transparent"
                          : "bg-[#1C1C1E] text-white/60 border-white/[0.06] hover:border-white/[0.15]"
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Prénom</label>
                  <input type="text" value={editingDealer.first_name || ""}
                    onChange={(e) => setEditingDealer({ ...editingDealer, first_name: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Nom</label>
                  <input type="text" value={editingDealer.last_name || ""}
                    onChange={(e) => setEditingDealer({ ...editingDealer, last_name: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                  />
                </div>
              </div>

              {editingDealer.client_type === "professionnel" && (
                <div>
                  <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Nom de l'entreprise</label>
                  <input type="text" value={editingDealer.company_name || ""}
                    onChange={(e) => setEditingDealer({ ...editingDealer, company_name: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                  />
                </div>
              )}

              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Email</label>
                <input type="email" value={editingDealer.email || ""}
                  onChange={(e) => setEditingDealer({ ...editingDealer, email: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                />
              </div>

              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Téléphone</label>
                <input type="tel" value={editingDealer.phone || ""}
                  onChange={(e) => setEditingDealer({ ...editingDealer, phone: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                />
              </div>

              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Adresse</label>
                <input type="text" value={editingDealer.address_street || ""}
                  onChange={(e) => setEditingDealer({ ...editingDealer, address_street: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Code postal</label>
                  <input type="text" value={editingDealer.address_postal_code || ""}
                    onChange={(e) => setEditingDealer({ ...editingDealer, address_postal_code: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Ville</label>
                  <input type="text" value={editingDealer.address_city || ""}
                    onChange={(e) => setEditingDealer({ ...editingDealer, address_city: e.target.value })}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Pays</label>
                <input type="text" value={editingDealer.address_country || "France"}
                  onChange={(e) => setEditingDealer({ ...editingDealer, address_country: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-white/[0.06] flex gap-2 sticky bottom-0 bg-[#141414]/95 backdrop-blur">
              <button onClick={resetDealerPassword}
                className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5"
                title="Réinitialiser le mot de passe du client"
              >
                <ShieldCheck size={13} /> Reset MDP
              </button>
              <div className="flex-1" />
              <button onClick={() => setEditingDealer(null)}
                className="bg-white/[0.06] hover:bg-white/[0.1] text-white px-4 py-2 rounded-md text-sm transition-colors"
              >Annuler</button>
              <button onClick={saveEditDealer}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
              >Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT SERVICE TYPE MODAL ── */}
      {editingServiceType && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#141414] border border-white/10 rounded-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Edit size={16} className="text-blue-400" /> Modifier le service
              </h3>
              <button onClick={() => setEditingServiceType(null)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Nom</label>
                <input
                  type="text"
                  value={editingServiceType.name}
                  onChange={(e) => setEditingServiceType({ ...editingServiceType, name: e.target.value })}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                />
              </div>
              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Description</label>
                <textarea
                  value={editingServiceType.description || ""}
                  onChange={(e) => setEditingServiceType({ ...editingServiceType, description: e.target.value })}
                  rows={3}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-white/20"
                />
              </div>
              <div>
                <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Prix de base (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingServiceType.base_price}
                  onChange={(e) => setEditingServiceType({ ...editingServiceType, base_price: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-sm focus:outline-none focus:border-white/20"
                />
                <p className="text-[11px] text-white/35 mt-1.5">
                  Ce prix sera appliqué automatiquement aux nouvelles demandes utilisant ce service.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editingServiceType.is_active}
                  onChange={(e) => setEditingServiceType({ ...editingServiceType, is_active: e.target.checked })}
                  className="w-4 h-4 accent-emerald-500"
                />
                <span className="text-white text-sm">Service actif (visible par les clients)</span>
              </label>
            </div>
            <div className="px-5 py-4 border-t border-white/[0.06] flex gap-2">
              <button
                onClick={() => setEditingServiceType(null)}
                className="flex-1 bg-white/[0.06] hover:bg-white/[0.1] text-white py-2 rounded-md text-sm transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={saveEditService}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-md text-sm font-semibold transition-colors"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileServiceAdmin;
